import DOMPurify from 'dompurify';
import { MarketFactGrid } from '~/features/shared/market-content';
import type { NegotiationDraftApiResponse } from '../utils';

const labels: Record<string, string> = {
  startDate: 'Start date',
  endDate: 'End date',
  numOfShifts: 'Number of shifts',
  hoursPerShift: 'Hours per shift',
  pricePerCallShift: 'Rate per call shift',
  laborReqType: 'Labor requirement type',
  shiftPeriod: 'Shift period',
  shiftsPerPeriod: 'Shifts per period',
  callShiftsPerPeriod: 'Call shifts per period',
  numRvusReq: 'Required RVUs',
  minRvusPerShift: 'Minimum RVUs per shift',
  rvusPerPeriod: 'RVUs per period',
  rvuPeriod: 'RVU period',
  rvuBonusPeriod: 'RVU bonus period',
  rvuBonusMinimum: 'RVU bonus minimum',
  rvuBonusAmt: 'RVU bonus amount',
  callShiftDiffType: 'Call shift differential type',
  avgHourlyRate: 'Average hourly rate',
  numOfFtes: 'FTEs',
  fteShiftsPerYear: 'Shifts per FTE/year',
  yearlyRvusPerFte: 'Yearly RVUs per FTE',
  pricePerHour: 'Hourly rate',
  pricePerShift: 'Rate per shift',
  pricePerRvu: 'Rate per RVU',
  compType: 'Compensation type',
  laborType: 'Labor type',
  shiftDiffType: 'Shift differential type',
  totalContractValue: 'Total contract value',
  bonusesTxt: 'Bonuses & incentives',
  adminCostsTxt: 'Administrative costs',
  infrastructureTxt: 'Infrastructure requirements',
  extraTxt: 'Other terms',
};
export const negotiationTermLabel = (key: string) =>
  labels[key] ?? key.replace(/([a-z])([A-Z])/g, '$1 $2');
const richTextFields = new Set(['bonusesTxt', 'adminCostsTxt', 'infrastructureTxt', 'extraTxt']);
const formatTerm = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'Not specified';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

/** Shows the exact immutable API terms, including newly projected material fields. */
export const NegotiationTermsSummary = ({ terms }: Pick<NegotiationDraftApiResponse, 'terms'>) => (
  <section aria-label="Complete offer terms" className="space-y-4">
    <MarketFactGrid
      facts={Object.entries(terms)
        .filter(([key]) => !richTextFields.has(key))
        .map(([key, value]) => ({ label: negotiationTermLabel(key), value: formatTerm(value) }))}
    />
    {Object.entries(terms)
      .filter(([key]) => richTextFields.has(key))
      .map(([key, value]) => (
        <div key={key}>
          <h4 className="body-text-sb">{negotiationTermLabel(key)}</h4>
          <div
            className="body-text break-words"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(formatTerm(value), {
                ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li'],
                ALLOWED_ATTR: [],
              }),
            }}
          />
        </div>
      ))}
  </section>
);
